import * as React from "react"
import { connect } from "react-redux"
import { RootState } from "../../../../../store"
import { FC, useEffect, useRef, useState } from "react"
import {
  AdminYoutubeVideoFormProps,
  MainOwnerShareData,
  UserYoutubeVideoShareForm,
  YoutubeVideo
} from "../../../../../types"
import { saveYoutubeVideoShareForm, selectUser, selectYoutubeVideoShareData } from "../../../../../actions/adminActions"
import { roleOptions, ROLES } from "../../../../../constants"
import { handleAuthorizedRequest, releases } from "../../../../../apis"
import { uniqBy } from "lodash"
import adminYoutubeVideoValidator from "./validators/adminYoutubeVideoValidator"
import { FormApi } from "final-form"
import { mapListToCamelCase } from "../../../../../utility"
import { KButton, KDropdown, KInput, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { Dialog } from "@headlessui/react"
import { lang } from "../../../../../constants/languages"
import ToggleInput from "../../../../ui/components/ToggleInput"
import { MultiValue } from "react-select"

const AdminYoutubeVideoForm: FC<AdminYoutubeVideoFormProps> = (props) => {
  const { user } = props
  const [youtubeVideoOptions, setYoutubeVideoOptions] = useState<KSelectOption[]>([])
  const [youtubeVideoLoading, setYoutubeVideoLoading] = useState(false)
  const [searchedVideos, setSearchedVideos] = useState<YoutubeVideo[]>([])
  const [timeoutId, setTimeoutId] = useState(undefined)
  const [tooltipContent, setTooltipContent] = useState<string | undefined>(undefined)
  const [hasMainOwner, setHasMainOwner] = useState<boolean>(false)

  const formApiRef = useRef<FormApi<UserYoutubeVideoShareForm>>(null)

  const getVideoById = (videoId: number | undefined) => {
    if (!videoId || !props.shareData) return null
    if (videoId === props.shareData.youtubeVideo) {
      return props.shareData.youtubeVideoDetail
    }
    return searchedVideos.find((video) => video.id === videoId)
  }

  const onFormSubmit = async (values: UserYoutubeVideoShareForm) => {
    props.creatingRef.current = true
    props.saveYoutubeVideoShareForm({
      ...values,
      user: props.user?.id,
      title: `${props.user.firstName} ${props.user.lastName} - ${
        youtubeVideoOptions
          ? youtubeVideoOptions.find((trackOption) => trackOption?.value === values?.youtubeVideo)?.label.toString()
          : ""
      }`
    })
  }

  const createYoutubeVideoShareFormFromData = (): UserYoutubeVideoShareForm => {
    if (!props.shareData || !props.user) return new UserYoutubeVideoShareForm()
    const mainOwner = props.shareData.roleList?.some((role) => role.role === ROLES.MAIN_OWNER)
    const share = props.shareData.share
    const filteredRoleOptions: number[] = []
    const roleOptions = props.shareData?.roleList
    roleOptions?.forEach((option) => {
      if (option.role !== ROLES.MAIN_OWNER && option.role !== undefined && option?.roleDetail?.id !== undefined) {
        filteredRoleOptions.push(option.roleDetail.id)
      }
    })

    return {
      id: props.shareData.id,
      user: props.user.id,
      youtubeVideo: props.shareData.youtubeVideo,
      title: `${props.user.username} - ${props.shareData.youtubeVideoDetail?.title}`,
      roles: filteredRoleOptions,
      trackIsrc: props.shareData.youtubeVideoDetail.trackIsrc,
      share,
      mainOwner
    }
  }

  const [youtubeVideoShareData, setYoutubeVideoShareData] = useState<UserYoutubeVideoShareForm>(
    createYoutubeVideoShareFormFromData()
  )

  useEffect(() => {
    setYoutubeVideoShareData(createYoutubeVideoShareFormFromData())
  }, [props.shareData, props.user])

  useEffect(() => {
    const youtubeVideo = getVideoById(youtubeVideoShareData?.youtubeVideo)
    if (!youtubeVideo) {
      setTooltipContent(undefined)
    } else if (youtubeVideo) {
      const content = getTooltipContent(youtubeVideo?.mainOwnerShare)
      setTooltipContent(content)

      const mainOwnerAvailable = youtubeVideo?.mainOwnerShare?.user !== "0"
      setHasMainOwner(mainOwnerAvailable)
      if (youtubeVideoShareData?.mainOwner) {
        setYoutubeVideoShareData({
          ...youtubeVideoShareData,
          share: youtubeVideo.mainOwnerShare.share?.toString() || ""
        })
      }
    }
  }, [youtubeVideoShareData?.youtubeVideo])
  useEffect(() => {
    if (props.shareData?.youtubeVideoDetail.id) {
      setYoutubeVideoOptions(
        uniqBy(
          [
            ...youtubeVideoOptions,
            { value: props.shareData.youtubeVideo || -1, label: props.shareData.youtubeVideoDetail.title }
          ],
          "value"
        )
      )
    }
  }, [props.shareData])

  const onClickCross = () => {
    props.selectYoutubeVideoShareData(null)
  }

  const onSearchTextChanged = async (text: string) => {
    if (!text) return
    setYoutubeVideoOptions([])
    setYoutubeVideoLoading(true)
    clearTimeout(timeoutId)
    setTimeoutId(
      //@ts-ignore
      setTimeout(async () => {
        await handleAuthorizedRequest(async () => {
          const response = await releases(`/youtube-video/?search=${text}&limit=10`, "get")
          setSearchedVideos(mapListToCamelCase(response.data.results))
          setYoutubeVideoOptions(
            response.data.results.map((video) => ({
              value: video.id,
              label: video.title
            }))
          )
          setYoutubeVideoLoading(false)
        }, "")
      }, 500)
    )
  }

  const onSelectYoutubeVideo = (value: number) => {
    if (!value) {
      setYoutubeVideoShareData({ ...youtubeVideoShareData, youtubeVideo: undefined, trackIsrc: "" })
      return
    }
    const updatedIsrc = searchedVideos.find((video) => video?.id === value)?.trackIsrc
    if (!updatedIsrc) {
      setYoutubeVideoShareData({ ...youtubeVideoShareData, youtubeVideo: value })
    } else {
      setYoutubeVideoShareData({ ...youtubeVideoShareData, youtubeVideo: value, trackIsrc: updatedIsrc })
    }
  }

  const getTooltipContent = (mainOwnerShare: MainOwnerShareData): string | undefined => {
    if (!mainOwnerShare) return undefined
    if (mainOwnerShare.user === "0") {
      return `There is no main owner assigned to this video. Current main owner share is ${mainOwnerShare.share}%.`
    }
    return `Main owner of this video is ${mainOwnerShare.user} and their share is ${mainOwnerShare.share}%`
  }

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d] ">
        <div className="p-6 shadow-md w-[440px] rounded-[10px] bg-[#fff]">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-6 items-center justify-center">
              <span
                className="w-[72px] aspect-square rounded-full flex justify-center items-center"
                style={{
                  border: "1px solid #F3F3F3"
                }}
              >
                <img className="w-8 h-8" src="/admin_icons/music-album.svg" />
              </span>
              <KTitleSpan text={"Youtube Video Share Information"} fontSize={20} lineHeight="28px" color="#000" />
            </div>
            <div className="flex flex-row items-center gap-1">
              <KSpan text="User:" fontWeight={500} color="#000" />
              <KSpan text={`${user?.firstName} ${user?.lastName}`} color="#000" />
            </div>
            <div className="flex flex-col gap-2">
              <KDropdown
                key={`${youtubeVideoShareData.youtubeVideo}-${youtubeVideoShareData.trackIsrc}`}
                placeholder={"Search video"}
                padding="14px"
                gap="8px"
                menuWidth={400}
                menuLeftMargin={-40}
                selected={youtubeVideoOptions.find(
                  (youtubeVideoOption) => youtubeVideoOption?.value === youtubeVideoShareData?.youtubeVideo
                )}
                onInputChange={onSearchTextChanged}
                options={youtubeVideoOptions}
                isClearable={true}
                isEllipsis={true}
                onSelect={(selectedOption) => {
                  const selected = selectedOption as KSelectOption
                  onSelectYoutubeVideo(selected?.value)
                }}
                leftIcon="/admin_icons/user-gray.svg"
                rightIcon="/admin_icons/caret-down.svg"
                enableIndicator
              />
              <div className="flex">
                {tooltipContent !== undefined ? (
                  <KTooltip
                    backgroundColor="#000"
                    padding="6px 8px"
                    height="20px"
                    arrowColor="#000"
                    position="bottom"
                    content={
                      <div className="h-auto w-[200px] flex items-center">
                        <KSpan text={tooltipContent} fontSize={12} lineHeight="16px" color="#FFF" />
                      </div>
                    }
                  >
                    {" "}
                    <div className="flex flex-row gap-2 items-center py-2 px-3.5">
                      <ToggleInput
                        checked={youtubeVideoShareData?.mainOwner}
                        handleChange={() => {
                          setYoutubeVideoShareData({
                            ...youtubeVideoShareData,
                            mainOwner: !youtubeVideoShareData?.mainOwner
                          })
                        }}
                        disabled={hasMainOwner}
                      />
                      <KSpan text="Main Owner" color="#000" fontWeight={500} />
                    </div>
                  </KTooltip>
                ) : (
                  <div className="flex flex-row gap-2 items-center py-2 px-3.5">
                    <ToggleInput
                      checked={youtubeVideoShareData?.mainOwner}
                      handleChange={() => {
                        setYoutubeVideoShareData({
                          ...youtubeVideoShareData,
                          mainOwner: !youtubeVideoShareData?.mainOwner
                        })
                      }}
                    />
                    <KSpan text="Main Owner" color="#000" fontWeight={500} />
                  </div>
                )}
              </div>

              <KDropdown
                placeholder={"Select roles"}
                padding={youtubeVideoShareData.roles.length > 0 ? "6px" : "14px"}
                gap="8px"
                menuLeftMargin={-40}
                isMulti
                isEllipsis
                selected={[...roleOptions]?.filter((roleOption) =>
                  youtubeVideoShareData?.roles?.includes(roleOption?.value)
                )}
                onInputChange={onSearchTextChanged}
                options={roleOptions
                  .filter((opt) => !youtubeVideoShareData?.mainOwner || opt.value !== ROLES.OTHER)
                  .sort()}
                onSelect={(selectedOptions) => {
                  if (!selectedOptions || (selectedOptions as MultiValue<KSelectOption>).length === 0) {
                    setYoutubeVideoShareData({ ...youtubeVideoShareData, roles: [] })
                  } else {
                    const selectedRoles = (selectedOptions as MultiValue<KSelectOption>).map(
                      (selectedOption) => selectedOption.value
                    )
                    setYoutubeVideoShareData({ ...youtubeVideoShareData, roles: selectedRoles })
                  }
                }}
                leftIcon="/admin_icons/user-circle.svg"
                rightIcon="/admin_icons/caret-down.svg"
                enableIndicator
              />
              <KInput
                placeholder="User share"
                padding="14px"
                value={youtubeVideoShareData?.share || ""}
                onChange={(text: string) => {
                  setYoutubeVideoShareData({ ...youtubeVideoShareData, share: text })
                }}
                leftIcon="/admin_icons/users.svg"
              />
              <KInput
                placeholder="trackIsrc"
                padding="14px"
                value={youtubeVideoShareData?.trackIsrc || ""}
                onChange={(text: string) => {
                  setYoutubeVideoShareData({ ...youtubeVideoShareData, trackIsrc: text })
                }}
                leftIcon="/admin_icons/users.svg"
              />
            </div>
            <div className="flex gap-3 justify-center items-center">
              <KButton text={lang.common.cancel} onClick={props.onClose} background="#fff" shadowDisabled={true} />
              <KButton
                text={lang.common.save}
                textColor="white"
                background="black"
                onClick={() => {
                  const errors = adminYoutubeVideoValidator(youtubeVideoShareData)

                  if (Object.keys(errors).length === 0) {
                    onFormSubmit(youtubeVideoShareData).then(() => {
                      props.onClose()
                    })
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    shareData: state.admin.selectedYoutubeVideoShareData,
    user: state.admin.selectedUser
  }
}

export default connect(mapStateToProps, {
  selectYoutubeVideoShareData,
  selectUser,
  saveYoutubeVideoShareForm
})(AdminYoutubeVideoForm)
